/**
 * Desk workspace: inbox (chats + tickets, labelled), the offer card with a countdown, the
 * conversation and the User 360 — one column on phones, three panes on a desktop. Polls only while
 * visible (inbox 4 s, heartbeat 20 s); Web Push covers the background. A new offer rings + vibrates.
 */
import { cx, HopexEmblem, useToast } from '@hopex/ui';
import { BellOff, Check, Inbox as InboxIcon, Settings2, Sparkles, X } from 'lucide-react';
import { useCallback, useEffect, useMemo, useRef, useState } from 'react';
import { api, errorMessage } from '../lib/api.ts';
import { CATEGORY_FA, STATUS_FA } from '../lib/labels.ts';
import {
  chime,
  navigate,
  pushState,
  setBadge,
  usePath,
  useVisible,
  useVisualViewport,
  useWorkerMessages,
} from '../lib/platform.ts';
import type { Inbox, InboxItem, Me, View } from '../lib/types.ts';
import {
  CountdownRing,
  KindBadge,
  PriorityChip,
  span,
  TrustBadge,
  TypingDots,
  useNow,
  WhoAvatar,
} from './parts.tsx';
import { SettingsSheet } from './Settings.tsx';
import { ThreadScreen } from './Thread.tsx';
import { OpsScreen, opsEnabled, useOpsSummary } from './Ops.tsx';
import { User360Panel } from './User360.tsx';

const VIEWS: { id: View; label: string }[] = [
  { id: 'ACTIVE', label: 'همه‌ی باز' },
  { id: 'WAITING', label: 'منتظر پاسخ' },
  { id: 'QUEUE', label: 'صف' },
  { id: 'MINE', label: 'گفتگوهای من' },
  { id: 'CHATS', label: 'چت آنلاین' },
  { id: 'TICKETS', label: 'تیکت' },
  { id: 'DONE', label: 'بسته‌شده' },
];

export function Workspace({ me: initial, onSignedOut }: { me: Me; onSignedOut: () => void }) {
  const [me, setMe] = useState(initial);
  const [view, setView] = useState<View>('ACTIVE');
  const [inbox, setInbox] = useState<Inbox | null>(null);
  const [error, setError] = useState<string | null>(null);
  const [settings, setSettings] = useState(false);
  const [userPanel, setUserPanel] = useState<string | null>(null);
  const visible = useVisible();
  const path = usePath();
  const toast = useToast();
  const seenOffers = useRef(new Set<string>());
  const threadId = /^\/t\/([0-9a-f-]{36})$/.exec(path)?.[1] ?? null;
  const userRoute = /^\/u\/([0-9a-f-]{36})$/.exec(path)?.[1] ?? null;
  const ops = useOpsSummary();

  const load = useCallback(async () => {
    try {
      const r = await api.get<Inbox>(`/inbox?view=${view}`);
      setInbox(r);
      setError(null);
      setBadge(r.counters.offers + r.counters.mineWaiting + r.counters.queue);
      const fresh = r.items.filter((i) => i.offer?.toMe && !seenOffers.current.has(i.id));
      if (fresh.length) {
        fresh.forEach((i) => seenOffers.current.add(i.id));
        chime();
      }
    } catch (e) {
      setError(errorMessage(e));
    }
  }, [view]);

  useEffect(() => {
    const first = window.setTimeout(() => void load(), 0);
    if (!visible) return () => window.clearTimeout(first);
    const t = window.setInterval(() => void load(), 4_000);
    return () => {
      window.clearTimeout(first);
      window.clearInterval(t);
    };
  }, [load, visible]);

  // Presence: an open desk keeps the agent "seen" (offers need a heartbeat < 90 s).
  useEffect(() => {
    if (!visible) return;
    const beat = () => void api.post('/heartbeat').catch(() => undefined);
    beat();
    const t = window.setInterval(beat, 20_000);
    return () => window.clearInterval(t);
  }, [visible]);

  const reloadOps = ops.reload;
  useWorkerMessages(
    useCallback(() => {
      void load();
      reloadOps();
    }, [load, reloadOps]),
  );

  // OD-71: a new item in one of MY queues while the desk is open → chime + a notice with a link.
  const prevOps = useRef<Record<string, number> | null>(null);
  useEffect(() => {
    const qs = ops.summary?.queues;
    if (!qs) return;
    const now = Object.fromEntries(qs.map((q) => [q.key, q.count]));
    const before = prevOps.current;
    prevOps.current = now;
    if (!before) return;
    const grown = qs.filter((q) => q.count > (before[q.key] ?? 0));
    if (!grown.length) return;
    chime();
    toast.show({
      tone: 'info',
      title: `کار تازه: ${grown.map((q) => q.labelFa).join('، ')}`,
      description: 'از دکمه‌ی «عملیات» بالای صفحه تأیید یا رد کنید.',
    });
  }, [ops.summary, toast]);
  useVisualViewport();

  const offers = useMemo(() => (inbox?.items ?? []).filter((i) => i.offer?.toMe), [inbox]);
  const counts: Partial<Record<View, number>> = {
    WAITING: inbox?.counters.waiting,
    QUEUE: inbox?.counters.queue,
    MINE: inbox?.counters.mineWaiting,
  };

  const accept = async (id: string) => {
    try {
      await api.post(`/threads/${id}/accept`);
      navigate(`/t/${id}`);
      void load();
    } catch (e) {
      toast.show({ tone: 'danger', title: errorMessage(e) });
      void load();
    }
  };
  const decline = async (id: string) => {
    try {
      await api.post(`/threads/${id}/decline`);
      void load();
    } catch (e) {
      toast.show({ tone: 'danger', title: errorMessage(e) });
    }
  };

  const showList = !threadId && !userRoute;
  const inOps = path === '/ops' || path.startsWith('/ops/');
  const opsTotal = ops.summary?.queues.reduce((s, q) => s + q.count, 0) ?? 0;
  return (
    <div
      className="hx-ambient fixed inset-x-0 top-0 flex h-[var(--vvh,100dvh)] translate-y-[var(--vvt,0px)] flex-col overflow-hidden bg-canvas text-fg"
      data-desk-workspace
    >
      <TopBar
        me={me}
        onSettings={() => setSettings(true)}
        onStatus={(s) => setMe({ ...me, status: s })}
        hiddenOnMobile={inOps ? path !== '/ops' : !showList}
        ops={opsEnabled(ops.summary) ? { active: inOps, count: opsTotal } : null}
      />
      {inOps ? (
        <OpsScreen summary={ops.summary} reload={ops.reload} />
      ) : (
        <div className="flex min-h-0 flex-1">
          {/* Inbox column */}
          <section
            className={cx(
              'min-h-0 w-full flex-col border-line lg:flex lg:w-[400px] lg:shrink-0 lg:border-e',
              showList ? 'flex' : 'hidden',
            )}
            aria-label="صندوق گفتگوها"
          >
            {offers.length > 0 && (
              <div className="flex flex-col gap-2 px-3 pt-3" data-offers>
                {offers.map((o) => (
                  <OfferCard
                    key={o.id}
                    item={o}
                    onAccept={() => void accept(o.id)}
                    onDecline={() => void decline(o.id)}
                  />
                ))}
              </div>
            )}
            <nav
              className="flex shrink-0 gap-1.5 overflow-x-auto px-3 py-3 [scrollbar-width:none]"
              aria-label="نمای صندوق"
            >
              {VIEWS.map((v) => (
                <button
                  key={v.id}
                  type="button"
                  aria-pressed={view === v.id}
                  onClick={() => setView(v.id)}
                  className={cx(
                    'hx-press inline-flex min-h-9 shrink-0 cursor-pointer items-center gap-1.5 rounded-full border px-3.5 text-[12.5px] font-bold outline-hidden focus-visible:outline-2 focus-visible:outline-solid focus-visible:outline-focus',
                    view === v.id
                      ? 'border-accent bg-accent text-on-accent'
                      : 'border-line bg-surface-1 text-fg-muted hover:text-fg',
                  )}
                  data-view={v.id}
                >
                  {v.label}
                  {!!counts[v.id] && (
                    <span
                      className={cx(
                        'hx-num grid min-w-5 place-items-center rounded-full px-1 text-[10.5px]',
                        view === v.id ? 'bg-on-accent/20' : 'bg-accent text-on-accent',
                      )}
                    >
                      {(counts[v.id] ?? 0).toLocaleString('fa-IR')}
                    </span>
                  )}
                </button>
              ))}
            </nav>
            {!!inbox?.counters.unanswered && view !== 'DONE' && (
              <p
                className="m-0 mx-3 mb-2 flex items-center gap-2 rounded-[14px] bg-negative-tint px-3 py-2 text-[12px] font-semibold text-negative"
                role="status"
              >
                <span className="dk-live size-2 rounded-full bg-negative" />
                {inbox.counters.unanswered.toLocaleString('fa-IR')} گفتگو بی‌پاسخ مانده؛ هر کارشناسی
                می‌تواند بردارد.
              </p>
            )}
            <div className="min-h-0 flex-1 overflow-y-auto px-3 pb-[calc(var(--hx-sab,env(safe-area-inset-bottom))+16px)]">
              {error && !inbox ? (
                <p role="alert" className="m-0 mt-6 text-center text-[13px] text-negative">
                  {error}
                </p>
              ) : !inbox ? (
                <ListSkeleton />
              ) : inbox.items.length === 0 ? (
                <EmptyInbox view={view} />
              ) : (
                <ul className="dk-stagger m-0 flex list-none flex-col gap-2 p-0" data-inbox-list>
                  {inbox.items.map((i) => (
                    <InboxRow key={i.id} item={i} active={i.id === threadId} />
                  ))}
                </ul>
              )}
              {error && inbox && (
                <p role="status" className="m-0 mt-3 text-center text-[11.5px] text-fg-subtle">
                  به‌روزرسانی ناموفق بود؛ دوباره تلاش می‌کنیم…
                </p>
              )}
            </div>
          </section>

          {/* Conversation */}
          <section
            className={cx('min-h-0 min-w-0 flex-1 flex-col', threadId ? 'flex' : 'hidden lg:flex')}
            aria-label="گفتگو"
          >
            {threadId ? (
              <ThreadScreen
                key={threadId}
                id={threadId}
                me={me}
                onOpenUser={(uid) =>
                  window.matchMedia('(min-width: 1280px)').matches
                    ? setUserPanel(uid)
                    : navigate(`/u/${uid}`)
                }
                onChanged={() => void load()}
              />
            ) : (
              <Welcome me={me} offers={offers.length} />
            )}
          </section>

          {/* User 360: side panel on wide screens, its own page on phones */}
          {userPanel && !userRoute && (
            <aside
              className="dk-sheet-in hidden min-h-0 w-[400px] shrink-0 flex-col border-s border-line bg-surface-1 xl:flex"
              aria-label="پرونده‌ی کاربر"
            >
              <User360Panel
                key={userPanel}
                userId={userPanel}
                onClose={() => setUserPanel(null)}
                canOps={opsEnabled(ops.summary)}
              />
            </aside>
          )}
          {userRoute && (
            <section
              className="flex min-h-0 w-full min-w-0 flex-1 flex-col bg-surface-1 lg:absolute lg:inset-y-0 lg:end-0 lg:z-30 lg:w-[440px] lg:border-s lg:border-line lg:shadow-float"
              aria-label="پرونده‌ی کاربر"
            >
              <User360Panel
                key={userRoute}
                userId={userRoute}
                onClose={() => window.history.back()}
                canOps={opsEnabled(ops.summary)}
              />
            </section>
          )}
        </div>
      )}
      {settings && (
        <SettingsSheet
          me={me}
          onClose={() => setSettings(false)}
          onMe={setMe}
          onSignedOut={onSignedOut}
        />
      )}
    </div>
  );
}

function TopBar({
  me,
  onSettings,
  onStatus,
  hiddenOnMobile,
  ops,
}: {
  me: Me;
  onSettings: () => void;
  onStatus: (s: 'ONLINE' | 'AWAY') => void;
  hiddenOnMobile: boolean;
  /** OD-71: «عملیات» switch for agents with ticked groups. */
  ops: { active: boolean; count: number } | null;
}) {
  const toast = useToast();
  const online = me.status === 'ONLINE';
  const push = pushState();
  const toggle = async () => {
    const next = online ? 'AWAY' : 'ONLINE';
    try {
      await api.post('/status', { status: next });
      onStatus(next);
    } catch (e) {
      toast.show({ tone: 'danger', title: errorMessage(e) });
    }
  };
  return (
    <header
      className={cx(
        'relative shrink-0 items-center gap-3 border-b border-line bg-canvas-deep px-4 pb-3 pt-[calc(env(safe-area-inset-top)+12px)] lg:flex lg:pt-3',
        hiddenOnMobile ? 'hidden' : 'flex',
      )}
      data-theme-scope="dark"
    >
      <span
        aria-hidden
        className="pointer-events-none absolute inset-0 bg-[radial-gradient(60%_140%_at_100%_0%,color-mix(in_srgb,var(--hx-accent)_22%,transparent),transparent_65%)]"
      />
      <span className="relative grid size-10 shrink-0 place-items-center rounded-[14px] bg-accent text-on-accent shadow-cta">
        <HopexEmblem size={22} decorative />
      </span>
      <div className="relative min-w-0 flex-1">
        <p className="m-0 truncate text-[15px] font-extrabold text-fg">میز پشتیبانی</p>
        <p className="m-0 truncate text-[11.5px] text-fg-muted">{me.displayName}</p>
      </div>
      {ops && (
        <button
          type="button"
          onClick={() => navigate(ops.active ? '/' : '/ops')}
          aria-pressed={ops.active}
          data-desk-ops-switch
          className={cx(
            'hx-press relative inline-flex min-h-10 cursor-pointer items-center gap-1.5 rounded-full border px-3 text-[12.5px] font-bold',
            ops.active
              ? 'border-accent bg-accent text-on-accent'
              : 'border-line-strong bg-surface-2 text-fg',
          )}
        >
          {ops.active ? 'گفتگوها' : 'عملیات'}
          {!ops.active && ops.count > 0 && (
            <span className="hx-num grid min-w-5 place-items-center rounded-full bg-pending px-1 text-[10.5px] text-canvas">
              {ops.count.toLocaleString('fa-IR')}
            </span>
          )}
        </button>
      )}
      <button
        type="button"
        onClick={() => void toggle()}
        className={cx(
          'hx-press relative inline-flex min-h-10 cursor-pointer items-center gap-2 rounded-full border px-3.5 text-[12.5px] font-bold',
          online
            ? 'border-[color-mix(in_srgb,var(--hx-positive)_45%,transparent)] bg-positive-tint text-positive'
            : 'border-line-strong bg-surface-2 text-fg-muted',
        )}
        aria-pressed={online}
        data-desk-status={me.status}
      >
        <span
          className={cx(
            'size-2.5 rounded-full',
            online ? 'dk-live bg-positive shadow-[0_0_10px_var(--hx-positive)]' : 'bg-fg-subtle',
          )}
        />
        {online ? 'آنلاین' : 'دور از میز'}
      </button>
      <button
        type="button"
        onClick={onSettings}
        aria-label="تنظیمات و اعلان‌ها"
        className="hx-press relative grid size-10 cursor-pointer place-items-center rounded-full border border-line-strong bg-surface-2 text-fg-muted hover:text-fg"
        data-desk-settings
      >
        {push === 'granted' && me.device.push ? (
          <Settings2 aria-hidden className="size-5" />
        ) : (
          <BellOff aria-hidden className="size-5 text-pending" />
        )}
      </button>
    </header>
  );
}

function OfferCard({
  item,
  onAccept,
  onDecline,
}: {
  item: InboxItem;
  onAccept: () => void;
  onDecline: () => void;
}) {
  return (
    <article
      className="dk-offer dk-enter relative overflow-hidden rounded-[22px] border border-accent bg-surface-1 p-3.5 hx-frame"
      data-offer={item.ref}
    >
      <span aria-hidden className="dk-shimmer pointer-events-none absolute inset-0" />
      <div className="relative flex items-center gap-3">
        <CountdownRing expiresAt={item.offer?.expiresAt ?? item.updatedAt} />
        <div className="min-w-0 flex-1">
          <p className="m-0 flex items-center gap-1.5 text-[13.5px] font-extrabold">
            <Sparkles aria-hidden className="size-4 text-accent-text" />
            گفتگوی تازه برای شما
          </p>
          <p className="m-0 mt-0.5 truncate text-[12px] text-fg-muted">
            {item.who.name ?? 'مهمان'} · {item.lastMessage?.preview ?? 'در حال نوشتن پیام…'}
          </p>
        </div>
      </div>
      <div className="relative mt-3 flex gap-2">
        <button
          type="button"
          onClick={onAccept}
          className="hx-press inline-flex min-h-11 flex-1 cursor-pointer items-center justify-center gap-1.5 rounded-full bg-accent text-[14px] font-extrabold text-on-accent shadow-cta"
          data-offer-accept
        >
          <Check aria-hidden className="size-4.5" />
          پذیرش
        </button>
        <button
          type="button"
          onClick={onDecline}
          className="hx-press inline-flex min-h-11 cursor-pointer items-center justify-center gap-1.5 rounded-full border border-line-strong bg-surface-2 px-4 text-[13px] font-bold text-fg-muted"
          data-offer-decline
        >
          <X aria-hidden className="size-4" />
          ارجاع به همه
        </button>
      </div>
    </article>
  );
}

function InboxRow({ item, active }: { item: InboxItem; active: boolean }) {
  const now = useNow(1000);
  const waiting = item.waitingSince ? now - new Date(item.waitingSince).getTime() : null;
  const hot = waiting !== null && waiting > (item.kind === 'TICKET' ? 3_600_000 : 120_000);
  const unanswered = waiting !== null;
  return (
    <li>
      <a
        href={`/t/${item.id}`}
        onClick={(e) => {
          e.preventDefault();
          navigate(`/t/${item.id}`);
        }}
        className={cx(
          'hx-press hx-card relative flex gap-3 overflow-hidden rounded-[20px] border p-3.5 outline-hidden transition-colors focus-visible:outline-2 focus-visible:outline-solid focus-visible:outline-focus',
          active
            ? 'border-accent bg-accent-tint'
            : unanswered
              ? 'border-[color-mix(in_srgb,var(--hx-pending)_45%,transparent)] bg-[color-mix(in_srgb,var(--hx-pending)_8%,var(--hx-surface1))] hover:border-[color-mix(in_srgb,var(--hx-pending)_70%,transparent)]'
              : 'border-line bg-surface-1 hover:border-line-strong',
          (item.breached || (unanswered && hot)) &&
            'border-[color-mix(in_srgb,var(--hx-negative)_55%,transparent)]',
        )}
        data-thread-row={item.ref}
        data-unanswered={unanswered || undefined}
      >
        {/* Owner 2026-10-16: an unanswered customer must stand out at a glance. */}
        {unanswered && (
          <span
            aria-hidden
            className={cx(
              'absolute inset-y-3 start-0 w-1 rounded-e-full',
              hot || item.breached ? 'bg-negative' : 'bg-pending',
            )}
          />
        )}
        <WhoAvatar name={item.who.name} kind={item.kind} online={item.online} />
        <div className="min-w-0 flex-1">
          <div className="flex items-center gap-2">
            <p className="m-0 min-w-0 flex-1 truncate text-[14px] font-extrabold">
              {item.who.name ?? 'مهمان'}
            </p>
            {waiting !== null ? (
              <span
                className={cx(
                  'shrink-0 text-[11px] font-bold',
                  hot ? 'text-negative' : 'text-fg-subtle',
                )}
                title="مدت انتظار"
              >
                {span(waiting)}
              </span>
            ) : (
              <span className="shrink-0 text-[11px] text-fg-subtle">
                {STATUS_FA[item.status] ?? item.status}
              </span>
            )}
          </div>
          <div className="mt-1 flex flex-wrap items-center gap-1">
            <KindBadge kind={item.kind} />
            {item.kind !== 'TICKET' && !item.who.verified && (
              <TrustBadge verified={false} guest={item.who.guest} />
            )}
            <PriorityChip priority={item.priority} />
            {unanswered && !item.breached && (
              <span
                className={cx(
                  'inline-flex h-6 items-center gap-1.5 rounded-full border px-2 text-[11px] font-bold text-fg',
                  hot
                    ? 'border-[color-mix(in_srgb,var(--hx-negative)_55%,transparent)]'
                    : 'border-[color-mix(in_srgb,var(--hx-pending)_60%,transparent)]',
                )}
                data-waiting
              >
                <span
                  aria-hidden
                  className={cx(
                    'dk-live size-1.5 rounded-full',
                    hot ? 'bg-negative' : 'bg-pending',
                  )}
                />
                منتظر پاسخ ما
              </span>
            )}
            {item.breached && (
              <span
                className="inline-flex h-6 items-center rounded-full bg-negative px-2 text-[11px] font-bold text-white"
                data-breached
              >
                بی‌پاسخ
              </span>
            )}
          </div>
          <p className="m-0 mt-1.5 line-clamp-2 text-[12.5px] leading-6 text-fg-muted">
            {item.typing ? (
              <TypingDots label="در حال نوشتن…" />
            ) : item.lastMessage ? (
              <>
                {item.lastMessage.author === 'AGENT' && (
                  <span className="text-fg-subtle">شما: </span>
                )}
                {item.lastMessage.preview}
              </>
            ) : item.kind === 'TICKET' ? (
              item.subject
            ) : (
              CATEGORY_FA[item.category]
            )}
          </p>
          <div className="mt-1.5 flex items-center gap-2 text-[11px] text-fg-subtle">
            <bdi dir="ltr" className="font-mono">
              {item.ref}
            </bdi>
            <span aria-hidden>·</span>
            <span className="truncate">
              {item.assignee
                ? item.mine
                  ? 'با شما'
                  : `با ${item.assignee.name ?? 'همکار'}`
                : item.offer
                  ? 'در حال پیشنهاد'
                  : 'بدون کارشناس'}
            </span>
          </div>
        </div>
        {item.unread > 0 && (
          <span
            className="hx-num absolute end-3 top-3 grid min-w-5 place-items-center rounded-full bg-accent px-1.5 text-[11px] font-bold text-on-accent shadow-cta"
            data-unread={item.unread}
          >
            {item.unread.toLocaleString('fa-IR')}
          </span>
        )}
      </a>
    </li>
  );
}

function ListSkeleton() {
  return (
    <div className="flex flex-col gap-2" aria-hidden>
      {[0, 1, 2, 3].map((i) => (
        <div key={i} className="flex gap-3 rounded-[20px] border border-line bg-surface-1 p-3.5">
          <span className="size-11 animate-pulse rounded-full bg-surface-3" />
          <div className="flex flex-1 flex-col gap-2 pt-1">
            <span className="h-3 w-1/2 animate-pulse rounded-full bg-surface-3" />
            <span className="h-3 w-4/5 animate-pulse rounded-full bg-surface-2" />
          </div>
        </div>
      ))}
    </div>
  );
}

function EmptyInbox({ view }: { view: View }) {
  return (
    <div
      className="dk-enter mt-10 flex flex-col items-center gap-3 px-6 text-center"
      data-inbox-empty
    >
      <span className="grid size-16 place-items-center rounded-full bg-positive-tint text-positive">
        <InboxIcon aria-hidden className="size-7" />
      </span>
      <p className="m-0 text-[15px] font-extrabold">
        {view === 'DONE' ? 'گفتگوی بسته‌شده‌ای نیست' : 'همه پاسخ گرفته‌اند ✨'}
      </p>
      <p className="m-0 text-[12.5px] leading-6 text-fg-muted">
        {view === 'DONE'
          ? 'گفتگوهای حل‌شده و بسته‌شده اینجا می‌آیند.'
          : 'گفتگوی تازه که برسد، همین‌جا و با اعلان روی گوشی خبرتان می‌کنیم.'}
      </p>
    </div>
  );
}

function Welcome({ me, offers }: { me: Me; offers: number }) {
  return (
    <div className="grid h-full place-items-center p-8">
      <div className="dk-enter flex max-w-[360px] flex-col items-center gap-3 text-center">
        <span
          className="grid size-20 place-items-center rounded-[28px] bg-canvas-deep shadow-float hx-frame"
          data-theme-scope="dark"
        >
          <HopexEmblem size={40} decorative />
        </span>
        <p className="m-0 text-[18px] font-extrabold">سلام {me.displayName.split(/\s+/)[0]} 👋</p>
        <p className="m-0 text-[13px] leading-7 text-fg-muted">
          {offers > 0
            ? 'یک گفتگوی تازه منتظر پذیرش شماست.'
            : 'یک گفتگو را از فهرست باز کنید. پرونده‌ی کامل کاربر کنار گفتگو نمایش داده می‌شود.'}
        </p>
      </div>
    </div>
  );
}
